import React from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { setValidation, clearValidation } from '../../features/validation/validationSlice';
import { transformElements } from '../../features/layout/layoutSlice';
import { selectElement } from '../../features/selection/selectionSlice';
import {
  ALL_RAIL_TYPES,
  checkClearance,
  findRailSnapOffset,
  generateLoopPathPoints,
  validateLoopClosure,
} from '../../utils/topology';
import { LayoutElement } from '../../types';

const AUTO_SNAP_TOLERANCE = 72;
const AUTO_SNAP_MAX_PASSES = 3;
const POSITION_EPSILON = 0.001;

/**
 * Rail Topology validation panel (F2.2): runs the clearance checker and
 * loop-closure validator over the current layout and surfaces results.
 */
const ValidationPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const elements = useAppSelector((state) => state.layout.elements);
  const validation = useAppSelector((state) => state.validation);

  const autoSnapRailsForValidation = (
    list: LayoutElement[],
    tolerance: number,
    maxPasses: number
  ) => {
    const rails = list.filter(
      (el) => ALL_RAIL_TYPES.has(el.type) && el.metadata.visible && !el.metadata.locked
    );

    if (rails.length < 2) {
      return { snappedList: list, transforms: [] as Array<{ id: string; transform: { position: { x: number; y: number } } }> };
    }

    const workingById = new Map<string, LayoutElement>(
      list.map((el) => [
        el.id,
        {
          ...el,
          position: { ...el.position },
        },
      ])
    );

    for (let pass = 0; pass < maxPasses; pass++) {
      let movedAny = false;

      for (const rail of rails) {
        const current = workingById.get(rail.id);
        if (!current) continue;

        const others = Array.from(workingById.values()).filter(
          (el) =>
            el.id !== current.id &&
            ALL_RAIL_TYPES.has(el.type) &&
            el.metadata.visible &&
            !el.metadata.locked
        );

        const offset = findRailSnapOffset(current, others, tolerance);
        if (!offset) continue;

        const movedDistance = Math.hypot(offset.dx, offset.dy);
        if (movedDistance <= POSITION_EPSILON) continue;

        workingById.set(current.id, {
          ...current,
          position: {
            ...current.position,
            x: current.position.x + offset.dx,
            y: current.position.y + offset.dy,
          },
        });
        movedAny = true;
      }

      if (!movedAny) break;
    }

    const snappedList = Array.from(workingById.values());
    const transforms = rails
      .map((original) => {
        const snapped = workingById.get(original.id);
        if (!snapped) return null;

        const movedX = Math.abs(snapped.position.x - original.position.x) > POSITION_EPSILON;
        const movedY = Math.abs(snapped.position.y - original.position.y) > POSITION_EPSILON;
        if (!movedX && !movedY) return null;

        return {
          id: original.id,
          transform: {
            position: {
              x: snapped.position.x,
              y: snapped.position.y,
            },
          },
        };
      })
      .filter((entry): entry is { id: string; transform: { position: { x: number; y: number } } } => entry !== null);

    return { snappedList, transforms };
  };

  const runValidation = () => {
    const list = Object.values(elements);

    const { snappedList, transforms } = autoSnapRailsForValidation(
      list,
      AUTO_SNAP_TOLERANCE,
      AUTO_SNAP_MAX_PASSES
    );

    if (transforms.length > 0) {
      dispatch(transformElements(transforms));
    }

    const conflicts = checkClearance(snappedList);
    const loop = validateLoopClosure(snappedList);
    dispatch(
      setValidation({
        conflicts,
        openEndpoints: loop.openEndpoints,
        loops: loop.loops,
        railCount: loop.railCount,
        closed: loop.closed,
      })
    );
  };

  const handleValidateTopology = () => {
    runValidation();
  };

  const generateLoopPathJson = () => {
    const list = Object.values(elements);
    const waypoints = generateLoopPathPoints(list);
    
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(waypoints, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", "loop_path.json");
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const conflictCount = validation.conflicts.length;
  const openCount = validation.openEndpoints.length;
  const pathIssueCount = openCount + (validation.closed ? 0 : 1);
  const totalIssueCount = conflictCount + openCount;
  const hasIssues = conflictCount > 0 || openCount > 0 || !validation.closed;

  return (
    <div className="border-t border-slate-200 px-2.5 py-2.5 dark:border-gray-700">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold text-slate-800 dark:text-gray-100">
          Rail Topology
        </h2>
        <div className="flex items-center gap-1">
        <button
          onClick={handleValidateTopology}
          className="rounded-md bg-primary-600 px-2 py-1 text-[10px] font-medium text-white transition-colors hover:bg-primary-700"
        >
          Validate
        </button>
        {validation.hasRun && (
          <button
            onClick={() => dispatch(clearValidation())}
            className="rounded-md border border-slate-200 px-2 py-1 text-[10px] text-slate-500 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            title="Clear validation results"
          >
            Clear
          </button>
        )}
        </div>
      </div>

      {!validation.hasRun ? (
        <p className="text-[10px] leading-snug text-slate-400 dark:text-gray-500">
          Checks clearance and loop closure so no vehicle is stranded.
        </p>
      ) : (
        <div className="space-y-1.5">
          {/* Summary banner */}
          <div
            className={`rounded-md px-2 py-1 text-[11px] font-medium ${
              hasIssues
                ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                : 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
            }`}
          >
            {hasIssues
              ? `${Math.max(1, totalIssueCount)} issue(s) found`
              : 'No topology issues found'}
          </div>

          {hasIssues && (
            <p className="text-[10px] text-slate-400 dark:text-gray-500">
              Path issues: {pathIssueCount} · Clearance conflicts: {conflictCount}
            </p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-1">
            <Stat label="Rail segments" value={validation.railCount} />
            <Stat label="Loops" value={validation.loops} />
            <Stat
              label="Clearance conflicts"
              value={conflictCount}
              danger={conflictCount > 0}
            />
            <Stat label="Open ends" value={openCount} danger={openCount > 0} />
          </div>

          {/* Loop closure status */}
          <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-[10px] dark:bg-gray-800">
            <span className="text-slate-400 dark:text-gray-500">Loop closure</span>
            <span
              className={
                validation.closed
                  ? 'font-medium text-green-600 dark:text-green-400'
                  : 'font-medium text-amber-600 dark:text-amber-400'
              }
            >
              {validation.closed ? 'Closed' : 'Open / incomplete'}
            </span>
          </div>

          {!hasIssues && (
            <button
              onClick={generateLoopPathJson}
              className="w-full rounded-md bg-green-600 px-2 py-1 text-[10px] font-medium text-white transition-colors hover:bg-green-700"
            >
              Generate Loop Path
            </button>
          )}

          {/* Conflict list */}
          {conflictCount > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-medium text-slate-500 dark:text-gray-400">
                Clearance conflicts
              </div>
              <ul className="space-y-0.5">
                {validation.conflicts.map((c, i) => (
                  <li key={i} className="text-[10px]">
                    <button
                      onClick={() => dispatch(selectElement(c.aId))}
                      className="w-full rounded bg-red-50 px-1.5 py-0.5 text-left text-red-700 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/40"
                    >
                      {elements[c.aId]?.metadata.name ?? c.aId} ↔{' '}
                      {elements[c.bId]?.metadata.name ?? c.bId}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {openCount > 0 && (
            <p className="text-[10px] leading-snug text-amber-600 dark:text-amber-400">
              {openCount} open rail end(s) highlighted on the canvas may strand a
              vehicle.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; danger?: boolean }> = ({
  label,
  value,
  danger,
}) => (
  <div className="rounded-md bg-slate-50 px-2 py-1 dark:bg-gray-800">
    <div className="text-[9px] text-slate-400 dark:text-gray-500">{label}</div>
    <div
      className={`text-xs font-semibold ${
        danger ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'
      }`}
    >
      {value}
    </div>
  </div>
);

export default ValidationPanel;
